import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";

export const inp = "w-full bg-[#0d0d0d] border border-white/15 focus:border-gold outline-none px-3 py-2.5 text-sm text-white transition-colors";
export const lbl = "font-mono text-[9px] tracking-[0.25em] text-white/45 block mb-2";

export const Field = ({ label, children, className = "" }) => (
  <div className={className}><label className={lbl}>{label}</label>{children}</div>
);

export const TInput = ({ label, value, onChange, type = "text", placeholder = "", testid, className = "" }) => (
  <Field label={label} className={className}>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} data-testid={testid} className={inp} />
  </Field>
);

export const TArea = ({ label, value, onChange, rows = 4, testid, className = "" }) => (
  <Field label={label} className={className}>
    <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      data-testid={testid} className={inp} />
  </Field>
);

export const Sel = ({ label, value, onChange, options, testid, className = "" }) => (
  <Field label={label} className={className}>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className={`${inp} bg-[#0d0d0d]`}>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </Field>
);

export const Toggle = ({ label, checked, onChange, testid }) => (
  <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
    className="flex items-center gap-3 py-1">
    <span className={`w-9 h-5 rounded-full relative transition-colors ${checked ? "bg-gold" : "bg-white/15"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-black transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </span>
    <span className="font-mono text-[10px] tracking-[0.2em] text-white/60">{label}</span>
  </button>
);

const VARIANTS = {
  primary: "bg-gold text-black hover:bg-white",
  ghost: "border border-white/20 text-white/70 hover:border-gold hover:text-gold",
  danger: "border border-red-500/40 text-red-400 hover:bg-red-500/10",
};

export const Btn = ({ children, onClick, variant = "primary", disabled, testid, type = "button", className = "" }) => (
  <button type={type} onClick={onClick} disabled={disabled} data-testid={testid}
    className={`font-mono text-[10px] tracking-[0.2em] px-5 py-2.5 transition-colors disabled:opacity-40 ${VARIANTS[variant]} ${className}`}>
    {children}
  </button>
);

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
    <div>
      <h1 className="font-serif text-3xl md:text-4xl font-light text-white">{title}</h1>
      {sub && <p className="text-white/40 text-sm mt-2 max-w-2xl">{sub}</p>}
    </div>
    <div className="flex items-center gap-3">{children}</div>
  </div>
);

const TONES = {
  gold: "text-gold border-gold/40", dim: "text-white/40 border-white/15",
  red: "text-red-400 border-red-500/40", green: "text-emerald-400 border-emerald-500/40",
};

export const Badge = ({ children, tone = "dim" }) => (
  <span className={`font-mono text-[8px] tracking-[0.2em] border px-2 py-1 uppercase ${TONES[tone]}`}>{children}</span>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`border border-white/10 bg-[#0a0a0a] p-6 ${className}`} {...rest}>{children}</div>
);

export const statusTone = (s) =>
  s === "published" ? "gold" : s === "archived" ? "red" : s === "scheduled" ? "green" : "dim";

/* Editorial copy approval state — anything not APPROVED/PUBLISHED is hidden from the public site */
export const APPROVAL_STATES = ["PLACEHOLDER", "DRAFT", "APPROVED", "PUBLISHED"];
export const ApprovalSel = ({ value, onChange, testid }) => {
  const v = value || "PUBLISHED";
  const live = v === "APPROVED" || v === "PUBLISHED";
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-400" : "bg-amber-400"}`} />
      <select value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-[#0d0d0d] border border-white/15 font-mono text-[9px] tracking-[0.15em] text-white/70 px-2 py-1 outline-none focus:border-gold">
        {APPROVAL_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {!live && <span className="font-mono text-[8px] tracking-[0.1em] text-amber-400/80">HIDDEN PUBLICLY</span>}
    </span>
  );
};

/* Media picker: PICK / UPLOAD are the primary workflow; raw URL/id entry is behind MANUAL (advanced) */
export function MediaPicker({ label, value, onChange, mode = "url", filterType, testid, uploadClass = "PUBLIC" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [manual, setManual] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    if (open) adm.media(filterType ? `?mediaType=${filterType}` : "").then(setItems).catch(() => {});
  }, [open, filterType]);

  const choose = (m) => {
    if (mode === "url") {
      if (m.accessClass !== "PUBLIC") return;
      onChange(m.url);
    } else onChange(m.id);
    setOpen(false);
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("accessClass", uploadClass);
      fd.append("title", file.name);
      const m = await adm.uploadMedia(fd);
      onChange(mode === "url" ? (m.url || `/api/media/public/${m.id}`) : m.id);
      toast.success("Uploaded to the Media Library.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const isImg = mode === "url" && value && /(\.(jpg|jpeg|png|webp|gif)|\/media\/public\/)/i.test(value);

  return (
    <Field label={label}>
      <div className="flex items-center gap-2 flex-wrap">
        {isImg && <img src={value} alt="" className="w-9 h-9 object-cover border border-white/15" />}
        {value && !isImg && (
          <span className="font-mono text-[9px] text-white/50 max-w-[130px] truncate border border-white/10 px-2 py-1.5">{value}</span>
        )}
        <Btn variant="ghost" onClick={() => setOpen(true)} testid={testid ? `${testid}-pick` : undefined}>PICK</Btn>
        <Btn variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy}
          testid={testid ? `${testid}-upload` : undefined}>
          {busy ? "UPLOADING…" : value ? "REPLACE" : "UPLOAD"}
        </Btn>
        {value && (
          <button onClick={() => onChange(mode === "url" ? "" : null)} data-testid={testid ? `${testid}-clear` : undefined}
            className="text-white/35 hover:text-red-400 font-mono text-[9px] px-1">CLEAR</button>
        )}
        <button onClick={() => setManual(!manual)} data-testid={testid ? `${testid}-manual` : undefined}
          className={`font-mono text-[8px] tracking-[0.15em] px-1 transition-colors ${manual ? "text-gold" : "text-white/30 hover:text-white/60"}`}>
          MANUAL
        </button>
        <input type="file" hidden ref={fileRef}
          accept={filterType === "image" ? "image/*" : filterType === "audio" ? "audio/*" : undefined}
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      </div>
      {manual && (
        <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          placeholder={mode === "url" ? "Advanced: raw URL" : "Advanced: media id"} className={`${inp} mt-2`} />
      )}
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div className="bg-[#0a0a0a] border border-white/15 max-w-3xl w-full max-h-[75vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()} data-testid="media-picker-modal">
            <div className="flex justify-between items-center mb-5">
              <p className="font-mono text-[10px] tracking-[0.3em] text-gold">MEDIA LIBRARY {mode === "url" ? "— PUBLIC ASSETS" : ""}</p>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white">✕</button>
            </div>
            {items.length === 0 && <p className="text-white/40 text-sm">No media uploaded yet. Upload in the Media Library first.</p>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {items.filter((m) => (mode === "url" ? m.accessClass === "PUBLIC" : true)).map((m) => (
                <button key={m.id} onClick={() => choose(m)} className="border border-white/10 hover:border-gold p-2 text-left transition-colors">
                  {m.mediaType === "image" && m.url ? (
                    <img src={m.url} alt="" className="w-full aspect-square object-cover mb-2" />
                  ) : (
                    <div className="w-full aspect-square bg-[#111] flex items-center justify-center mb-2 font-mono text-[9px] text-white/40">
                      {m.mediaType?.toUpperCase()}
                    </div>
                  )}
                  <p className="text-[10px] text-white/70 truncate">{m.title || m.originalFilename}</p>
                  <p className="font-mono text-[8px] text-white/30 mt-1">{m.accessClass}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Field>
  );
}
